<template>
  <div class="page-outer banner-form-page">
    <div class="page-header page-header-fixed-form">
      <div class="header-left header-left--inline">
        <h1 class="page-title">
          <i class="ri-image-line"></i>
          <template v-if="isEdit">編輯 Banner</template>
          <template v-else>新增 Banner</template>
        </h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" :disabled="saving || loading || !!loadError" @click="goBack">取消</button>
        <button type="button" class="btn btn-primary" :disabled="saving || loading || !!loadError" @click="saveBanner">
          <span v-if="saving" class="spinner-sm"></span>
          {{ saving ? '儲存中...' : (isEdit ? '儲存變更' : '建立 Banner') }}
        </button>
      </div>
    </div>

    <div class="page-container">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>

      <div v-else-if="loadError" class="error-state">
        <i class="ri-error-warning-line"></i> {{ loadError }}
        <button type="button" class="btn btn-secondary btn-sm" @click="loadBanner">重新載入</button>
      </div>

      <div v-else class="form-container">
        <div class="form-section">
          <h3 class="section-title">Banner 內容</h3>
          <div class="form-group">
            <label class="form-label">版位</label>
            <select v-model="form.placement" class="form-control">
              <option v-for="p in PLACEMENT_OPTIONS" :key="p.value" :value="p.value">{{ p.label }}</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label required">Banner 圖片</label>
            <ImageUploader v-model="form.imageUrl" :uploadFn="uploadImage" shape="card" hint="建議尺寸 1200x400px（16:5）" />
          </div>
          <div v-if="form.placement === 'home'" class="form-group">
            <label class="form-label">首頁插槽</label>
            <select v-model="form.slot" class="form-control"><option value="hero">主視覺</option><option value="ads">廣告圖組</option><option value="campaign">活動橫幅</option></select>
          </div>
          <div class="form-group">
            <label class="form-label">手機圖片（選填）</label>
            <ImageUploader v-model="form.mobileImageUrl" :uploadFn="uploadImage" shape="card" hint="未設定時沿用桌機圖片" />
          </div>
          <div class="form-group">
            <label class="form-label">替代文字（Alt）</label>
            <input v-model="form.altText" class="form-control" placeholder="圖片說明（供無障礙與 SEO）" />
          </div>
          <div class="form-group">
            <label class="form-label">點擊連結</label>
            <input v-model="form.href" class="form-control" placeholder="https://..." />
          </div>
          <div class="form-group">
            <label class="toggle-row">
              <span>開新視窗</span>
              <input v-model="form.openInNewTab" type="checkbox" class="toggle-input" />
            </label>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">排序</label>
              <input v-model.number="form.sortOrder" type="number" min="0" class="form-control" />
            </div>
            <div class="form-group form-group--status">
              <span class="form-label">狀態</span>
              <div class="banner-active-toggle">
                <span class="banner-active-label" :class="{ 'banner-active-label--on': form.isActive }">
                  {{ form.isActive ? '啟用' : '停用' }}
                </span>
                <label class="switch switch--banner">
                  <input v-model="form.isActive" type="checkbox" />
                  <span class="slider"></span>
                </label>
              </div>
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">有效期間（選填）</label>
            <div class="date-range-row">
              <input v-model="form.validFrom" type="datetime-local" class="form-control" />
              <span class="date-sep">~</span>
              <input v-model="form.validTo" type="datetime-local" class="form-control" />
            </div>
            <p class="form-hint">留空表示長期有效。</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { usePageTitle } from '@/composables/usePageTitle.js'
import { commerceSettingsApi } from '@/api/modules/commerceSettings.js'
import { showError, showSuccess } from '@/utils/swal.js'
import ImageUploader from '@/components/ImageUploader.vue'
import apiClient from '@/api/axios.js'

const PLACEMENT_OPTIONS = [
  { value: 'home', label: '首頁' },
  { value: 'category', label: '分類頁' },
  { value: 'product', label: '商品頁' },
  { value: 'storefront_page', label: '前台頁面' },
  { value: 'hall', label: '館別頁' },
  { value: 'checkout', label: '結帳頁' },
  { value: 'announcement', label: '公告欄' },
]

const route = useRoute()
const router = useRouter()

const isEdit = computed(() => route.name === 'ClientCommerceBannerEdit')

usePageTitle(isEdit.value ? '編輯 Banner' : '新增 Banner')

const loading = ref(false)
const loadError = ref('')
const saving = ref(false)
const originalBannerId = ref('')

const defaultPlacement = () => {
  const q = route.query.placement
  if (typeof q === 'string' && PLACEMENT_OPTIONS.some((p) => p.value === q)) return q
  return 'home'
}

const form = ref({
  placement: defaultPlacement(),
  imageUrl: '', mobileImageUrl: '', slot: 'hero',
  altText: '',
  href: '',
  openInNewTab: false,
  sortOrder: 0,
  isActive: true,
  validFrom: '',
  validTo: '',
})

function goBack() {
  router.push({ path: '/client/commerce/banners', query: { placement: form.value.placement } })
}

function initEmptyForm() {
  form.value = {
    placement: defaultPlacement(),
    imageUrl: '', mobileImageUrl: '', slot: 'hero',
    altText: '',
    href: '',
    openInNewTab: false,
    sortOrder: 0,
    isActive: true,
    validFrom: '',
    validTo: '',
  }
}

async function loadBanner() {
  if (!isEdit.value) {
    initEmptyForm()
    return
  }
  const id = route.params.bannerId
  if (!id) {
    loadError.value = '缺少 Banner ID'
    return
  }
  loading.value = true
  loadError.value = ''
  try {
    const res = await commerceSettingsApi.getBanner(id)
    const b = res?.data ?? {}
    originalBannerId.value = b.id ?? id
    form.value = {
      placement: b.placement ?? 'home',
      imageUrl: b.image_url ?? '', mobileImageUrl: b.mobile_image_url ?? '', slot: b.slot ?? 'hero',
      altText: b.alt_text ?? '',
      href: b.href ?? '',
      openInNewTab: b.open_in_new_tab === true,
      sortOrder: b.sort_order ?? 0,
      isActive: b.is_active !== false,
      validFrom: b.valid_from ? b.valid_from.slice(0, 16) : '',
      validTo: b.valid_to ? b.valid_to.slice(0, 16) : '',
    }
  } catch (e) {
    loadError.value = e?.response?.data?.message || '載入失敗'
  } finally {
    loading.value = false
  }
}

async function uploadImage(file) {
  const fd = new FormData()
  fd.append('file', file)
  return apiClient.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
}

async function saveBanner() {
  if (!form.value.imageUrl?.trim()) {
    await showError('請上傳 Banner 圖片')
    return
  }
  saving.value = true
  try {
    if (isEdit.value) {
      if (!originalBannerId.value) {
        await showError('找不到 Banner 識別')
        return
      }
      await commerceSettingsApi.updateBanner(originalBannerId.value, form.value)
      await showSuccess('Banner 更新成功')
    } else {
      await commerceSettingsApi.createBanner(form.value)
      await showSuccess('Banner 建立成功')
    }
    router.push({ path: '/client/commerce/banners', query: { placement: form.value.placement } })
  } catch (e) {
    await showError(e?.response?.data?.message || '儲存失敗')
  } finally {
    saving.value = false
  }
}

onMounted(loadBanner)
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  margin-bottom: 0;
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.header-left--inline {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.header-actions { display: flex; align-items: center; gap: 8px; }

.page-header .page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.page-container {
  padding: 20px;
  padding-top: 72px;
}

.btn { padding: 6px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; border: none; display: inline-flex; align-items: center; gap: 6px; }
.btn-primary { background: #3b82f6; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #fff; color: #374151; border: 1px solid #d1d5db; }
.btn-secondary:hover:not(:disabled) { background: #f9fafb; }
.btn-secondary:disabled { opacity: 0.6; cursor: not-allowed; }

.spinner-sm {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: banner-form-spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes banner-form-spin {
  to { transform: rotate(360deg); }
}

.loading-state,
.error-state {
  background: #fff;
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  color: #6b7280;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
}
.error-state { color: #b91c1c; }
.error-state .btn { margin-left: 10px; }

.form-container {
  max-width: 900px;
  margin: 0 auto;
  background: #fff;
  border-radius: 12px;
  padding: 28px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.form-section { margin-bottom: 0; }

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #222;
  margin: 0 0 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #f0f0f0;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.form-group { display: flex; flex-direction: column; margin-bottom: 16px; }
.form-group:last-child { margin-bottom: 0; }
.form-group--status {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.banner-active-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
  min-height: 44px;
  box-sizing: border-box;
}
.banner-active-label {
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
}
.banner-active-label--on {
  color: #166534;
}
.switch {
  position: relative;
  display: inline-block;
  width: 46px;
  height: 24px;
  flex-shrink: 0;
}
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.switch .slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #d1d5db;
  border-radius: 24px;
  transition: background 0.2s;
}
.switch .slider:before {
  position: absolute;
  content: '';
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background: white;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s;
}
.switch--banner input:checked + .slider {
  background: #3b82f6;
}
.switch--banner input:checked + .slider:before {
  transform: translateX(22px);
}
.switch--banner input:focus-visible + .slider {
  outline: 2px solid #93c5fd;
  outline-offset: 2px;
}

.form-label { font-size: 13px; font-weight: 500; color: #555; margin-bottom: 5px; }
.form-label.required::after { content: ' *'; color: #e74c3c; }

.form-control {
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
}
.form-control:focus {
  outline: none;
  border-color: #1a73e8;
  box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.15);
}

.toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  user-select: none;
}
.toggle-input {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.date-range-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.date-sep { color: #6b7280; flex-shrink: 0; }
.form-hint { font-size: 12px; color: #9ca3af; margin: 6px 0 0; }

@media (max-width: 768px) {
  .page-container { padding: 12px; padding-top: 72px; }
  .form-container { padding: 16px; }
  .form-row { grid-template-columns: 1fr; }
}
</style>
